import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useCurrency from '../../../../../components/CurrencyProvider';
import { formatDateDMY } from '../../../../../utils/dateUtils';
import { PackageInfo } from './consumptionDetails.helper';

interface PackageSummaryCardProps {
  packageInfo: PackageInfo | null | undefined;
}

const PackageSummaryCard: React.FC<PackageSummaryCardProps> = ({ packageInfo }) => {
  const { t: tBilling } = useTranslation('Packagebilling');
  const { formatCurrency } = useCurrency();

  if (!packageInfo) {
    return null;
  }

  const labelSx = { color: '#718096', textTransform: 'uppercase', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1 } as const;
  const valueSx = { fontWeight: 500, color: '#2d3748', fontSize: '0.8rem' } as const;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        bgcolor: '#ffffff',
      }} 
      data-testid="consumption-package-summary-card"
    >
      {/* Header */}
      <Box sx={{ backgroundColor: '#f7fafc', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
          {tBilling('consumption_details.report.package_details')}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1 }}>
        {/* Row 1: Package Name & Code, Validity Period, Package Amount, Concession Amount */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(5, 1fr)', 
          gap: 1.5,
          mb: 1,
          pb: 1,
          borderBottom: '1px dashed #e2e8f0',
        }}>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.package_name')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a365d', fontSize: '0.8rem' }}>
              {packageInfo.package_name ?? '-'} (#{packageInfo.code})
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.validity_period')}
            </Typography>
            <Typography variant="body2" sx={valueSx}>
              {formatDateDMY(packageInfo.start_date)} - {formatDateDMY(packageInfo.valid_until)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.package_amount')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#805ad5', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.package_amount ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.concession_amount')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.8rem' }}>
              {formatCurrency((packageInfo.concession_amount ?? 0) + (packageInfo.total_service_concession_amount ?? 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.net_amount')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.net_amount ?? 0)}
            </Typography>
          </Box>
        </Box>

        {/* Row 2: Net Amount, Advance Paid, Advance Remaining, Advance Balance, Service Amount, Service Net Amount */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(5, 1fr)', 
          gap: 1.5,
          mb: 1,
          pb: 1,
          borderBottom: '1px dashed #e2e8f0',
        }}>

          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.advance_paid')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.advance_paid ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.advance_remaining')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3182ce', fontSize: '0.8rem' }}>
              {formatCurrency((packageInfo.net_amount ?? 0) - (packageInfo.advance_paid ?? 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, color: (packageInfo.advance_balance_amount ?? 0) < 0 ? '#c53030' : '#dd6b20' }}>
              {tBilling('consumption_details.report.advance_balance')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: (packageInfo.advance_balance_amount ?? 0) < 0 ? '#c53030' : '#dd6b20', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.advance_balance_amount ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.service_amount')}
            </Typography>
            <Typography variant="body2" sx={valueSx}>
              {formatCurrency(packageInfo.service_amount ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.service_net_amount')}
            </Typography>
            <Typography variant="body2" sx={valueSx}>
              {formatCurrency(packageInfo.service_net_amount ?? 0)}
            </Typography>
          </Box>
        </Box>

        {/* Row 3: Service Consumed, Service Pending, Pharmacy Amount, Pharmacy Consumed, Pharmacy Pending */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(5, 1fr)', 
          gap: 1.5,
        }}>
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, color: '#38a169' }}>
              {tBilling('consumption_details.report.service_consumed')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.service_consumed ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, color: '#dd6b20' }}>
              {tBilling('consumption_details.report.service_pending')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#dd6b20', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.service_pending ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              {tBilling('consumption_details.report.pharmacy_amount')}
            </Typography>
            <Typography variant="body2" sx={valueSx}>
              {formatCurrency(packageInfo.pharmacy_amount ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, color: '#38a169' }}>
              {tBilling('consumption_details.report.pharmacy_consumed')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#38a169', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.pharmacy_consumed ?? 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, color: '#dd6b20' }}>
              {tBilling('consumption_details.report.pharmacy_pending')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#dd6b20', fontSize: '0.8rem' }}>
              {formatCurrency(packageInfo.pharmacy_pending ?? 0)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PackageSummaryCard;
